/**
 * Carga del editor de plantillas: la config de la marca (del contexto), la plantilla o el modelo de
 * arranque, y los campos para «Insertar variable». La usan las dos páginas del editor: la de siempre
 * (`PlantillaEditorPage`) y la plantilla de una campaña (`CampanaPlantillaPage`, `campanas/:id/plantilla`).
 * Las dos arrancan igual y sólo cambia qué hacen cuando la plantilla ya no es un borrador
 * (`alNoSerBorrador`).
 *
 * El editor va aparte porque `useValidacionPlantilla` necesita un borrador desde el primer render.
 *
 * `marcarCreada(id)`: cuando el editor crea la plantilla, quien lo muestra cambia la dirección (la
 * página de siempre pasa de /nueva a /:id/editar; la de la campaña, de `editor=nueva` a
 * `editor=plantilla:<id>`). Con la marca, ese cambio no la vuelve a traer: lo que está en pantalla ES lo
 * que se guardó, y traerla de nuevo pisaría lo que el operador siguió escribiendo. La marca se gasta en
 * la primera dirección distinta: volver después a la de la creada la trae del servidor.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { CampoDisponible, ModeloPlantilla, PlantillaBorrador, PlantillaDetalle } from "../types";
import { oficialApi } from "../api";
import { useCuentaOficial } from "../contexto";
import { borradorVacio, desdeDetalle, desdeModelo } from "../logic/plantilla";
import { leerError, leerRespuesta, textoError } from "../logic/respuesta";

export type CargaEditorPlantilla =
  | { estado: "cargando" }
  | { estado: "error"; error: string }
  | { estado: "no_existe" }
  | { estado: "listo"; inicial: PlantillaBorrador; id?: string; aviso?: string; version?: number; clave: number };

export interface OpcionesCargaEditor {
  /** Plantilla a editar (tiene que ser un borrador). */
  id?: string;
  /** Clave de un modelo Deenex para arrancar (sólo sin `id`). */
  modelo?: string | null;
  /** La plantilla ya se mandó a Meta: no se edita. La página de siempre navega al detalle; la de la campaña vuelve al asistente con el aviso. */
  alNoSerBorrador: (p: PlantillaDetalle) => void;
}

export function useCargaEditorPlantilla({ id, modelo, alNoSerBorrador }: OpcionesCargaEditor): {
  carga: CargaEditorPlantilla; campos: CampoDisponible[]; camposFallaron: boolean; reintentar: () => void; marcarCreada: (id: string) => void;
} {
  const { config } = useCuentaOficial();
  const [carga, setCarga] = useState<CargaEditorPlantilla>({ estado: "cargando" });
  const [campos, setCampos] = useState<CampoDisponible[]>([]);
  const [camposFallaron, setCamposFallaron] = useState(false);
  const [intento, setIntento] = useState(0);
  const idCreado = useRef<string | null>(null);
  const claveCarga = useRef(0);
  // En un ref: quien usa el hook suele pasar una función nueva en cada render, y ponerla en las
  // dependencias volvería a traer la plantilla en cada render.
  const alNoSerBorradorRef = useRef(alNoSerBorrador);
  alNoSerBorradorRef.current = alNoSerBorrador;

  // Campos del cliente para «Insertar variable»: independiente de la plantilla. Son los de Activa a
  // propósito: `campoSugerido` es un dato fijo y una plantilla no conoce la base de ninguna marca.
  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const l = leerRespuesta<CampoDisponible[]>(await oficialApi.campos());
        if (!vivo) return;
        if (l.ok && Array.isArray(l.data)) { setCampos(l.data); setCamposFallaron(false); }
        else setCamposFallaron(true);
      } catch {
        if (vivo) setCamposFallaron(true);
      }
    })();
    return () => { vivo = false; };
  }, []);

  useEffect(() => {
    if (!config) return;
    if (id && id === idCreado.current) return;
    // La marca vale para el cambio de dirección que sigue a crearla, no para siempre: con un «atrás» o
    // un «adelante» dentro de la misma página (la galería, una copia) se vuelve a esa dirección, y lo que
    // está en pantalla ya es otra cosa. Hay que traerla del servidor.
    idCreado.current = null;
    let vivo = true;
    setCarga({ estado: "cargando" });
    const listo = (inicial: PlantillaBorrador, extra: { id?: string; aviso?: string; version?: number } = {}) => {
      claveCarga.current += 1;
      setCarga({ estado: "listo", inicial, clave: claveCarga.current, ...extra });
    };
    (async () => {
      try {
        if (id) {
          const l = leerRespuesta<PlantillaDetalle>(await oficialApi.plantilla(id));
          if (!vivo) return;
          if (!l.ok) { setCarga({ estado: "error", error: textoError(l) }); return; }
          const p = l.data;
          if (!p) { setCarga({ estado: "error", error: "El servidor no devolvió la plantilla." }); return; }
          if (p.estado !== "borrador") { alNoSerBorradorRef.current(p); return; }
          listo(desdeDetalle(p), { id: p.id, version: p.version });
          return;
        }
        if (modelo) {
          const l = leerRespuesta<ModeloPlantilla[]>(await oficialApi.modelos());
          if (!vivo) return;
          if (!l.ok || !Array.isArray(l.data)) {
            const motivo = l.ok ? "el servidor no devolvió la lista" : textoError(l);
            listo(borradorVacio(config), { aviso: `No pudimos traer los modelos (${motivo}). Arrancás con una plantilla vacía.` });
            return;
          }
          const m = l.data.find((x) => x.clave === modelo);
          if (!m) { listo(borradorVacio(config), { aviso: "Ese modelo no existe. Arrancás con una plantilla vacía." }); return; }
          // El modelo trae el idioma del backend; la marca pudo haber elegido otro en Configuración.
          listo({ ...desdeModelo(m), idioma: config.idioma });
          return;
        }
        listo(borradorVacio(config));
      } catch (e) {
        if (!vivo) return;
        const x = leerError(e);
        if (x.status === 404) setCarga({ estado: "no_existe" });
        else setCarga({ estado: "error", error: textoError(x) });
      }
    })();
    return () => { vivo = false; };
    // `config` cambia de referencia cuando se recarga la cuenta; recargar el borrador por eso
    // pisaría lo que el operador está escribiendo. Sólo importa que exista.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, modelo, !!config, intento]);

  const reintentar = useCallback(() => setIntento((n) => n + 1), []);
  const marcarCreada = useCallback((nuevo: string) => { idCreado.current = nuevo; }, []);

  return { carga, campos, camposFallaron, reintentar, marcarCreada };
}
